import React, { useRef } from 'react';
import { Bell, Mail, MessageSquare, Calendar, CheckCircle } from 'lucide-react';
import { useElectionReminderOptIn, formatElectionReminderData } from '../hooks/useElectionReminderOptIn';
import toast from 'react-hot-toast';

interface ElectionRemindersProps {
  engagementData: any;
  updateEngagementData: (patch: any) => void;
  onNext: () => void;
  onPrevious: () => void;
  setShowReturnFlow: (arg0: boolean) => void;
}

export default function ElectionReminders({ engagementData, updateEngagementData, onNext, onPrevious, setShowReturnFlow }: ElectionRemindersProps) {
  const electionReminderMutation = useElectionReminderOptIn();
  const loadingToastId = useRef<string | null>(null);
  

  const handleReminderTypeChange = (type: 'email' | 'text', checked: boolean) => {
    updateEngagementData({
      [type === 'email' ? 'emailReminders' : 'textReminders']: checked
    });
  };

  const handleTimingChange = (timing: '7Days' | '1Day' | 'ElectionDay', checked: boolean) => {
    updateEngagementData({
      [`reminder${timing}`]: checked
    });
  };

  const isFormValid = engagementData.emailReminders || engagementData.textReminders;

  const handleSavePreferences = () => {
    if (!isFormValid) return;

    const reminderData = formatElectionReminderData(engagementData);
    
    electionReminderMutation.mutate({
      url: '/election-reminder/opt-in',
      method: 'POST',
      data: reminderData
    }, {
      onSuccess: () => {
        toast.success('Election reminders preferences saved successfully!');
        onNext();
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to save reminder preferences. Please try again.';
        toast.error(errorMessage);
      }
    });
  };

  // Handle loading state
  React.useEffect(() => {
    if (electionReminderMutation.isPending && !loadingToastId.current) {
      loadingToastId.current = toast.loading('Saving your preferences...');
    }

    if (!electionReminderMutation.isPending && loadingToastId.current) {
      toast.dismiss(loadingToastId.current);
      loadingToastId.current = null;
    }

    return () => {
      if (loadingToastId.current) {
        toast.dismiss(loadingToastId.current);
        loadingToastId.current = null;
      }
    };
  }, [electionReminderMutation.isPending]);

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full mb-4">
          <Bell className="w-8 h-8 text-white" />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-3">Get Election Reminders</h1>
        <p className="text-gray-600 text-lg">Choose how and when you'd like to receive reminders.</p>
        <p className="text-gray-500 text-sm mt-2">This is optional and can be changed anytime.</p>
      </div>

      {/* Reminder Types */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
          Choose Your Reminder Method
        </h2>
        
        <div className="space-y-3">
          <label className="group relative flex items-center p-4 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:border-blue-300 hover:bg-blue-50">
            <input
              type="checkbox"
              checked={engagementData.emailReminders}
              onChange={(e) => handleReminderTypeChange('email', e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <div className="ml-4 flex items-center flex-1">
              <Mail className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <span className="font-medium text-gray-900">Email reminders</span>
                <p className="text-sm text-gray-500">Receive reminders via email</p>
              </div>
            </div>
            {engagementData.emailReminders && (
              <CheckCircle className="w-5 h-5 text-blue-600" />
            )}
          </label>

          <label className="group relative flex items-center p-4 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:border-blue-300 hover:bg-blue-50">
            <input
              type="checkbox"
              checked={engagementData.textReminders}
              onChange={(e) => handleReminderTypeChange('text', e.target.checked)}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
            />
            <div className="ml-4 flex items-center flex-1">
              <MessageSquare className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <span className="font-medium text-gray-900">Text message reminders</span>
                <p className="text-sm text-gray-500">Get SMS notifications</p>
              </div>
            </div>
            {engagementData.textReminders && (
              <CheckCircle className="w-5 h-5 text-blue-600" />
            )}
          </label>
        </div>
      </div>

      {/* Reminder Timing */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <div className="w-2 h-2 bg-purple-500 rounded-full mr-2"></div>
          Reminder Timing Options
        </h2>
        
        <div className="space-y-3">
          <label className="group relative flex items-center p-4 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:border-purple-300 hover:bg-purple-50">
            <input
              type="checkbox"
              checked={engagementData.reminder7Days}
              onChange={(e) => handleTimingChange('7Days', e.target.checked)}
              className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
            />
            <div className="ml-4 flex items-center flex-1">
              <Calendar className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <span className="font-medium text-gray-900">7 days before election</span>
                <p className="text-sm text-gray-500">Early planning reminder</p>
              </div>
            </div>
            {engagementData.reminder7Days && (
              <CheckCircle className="w-5 h-5 text-purple-600" />
            )}
          </label>

          <label className="group relative flex items-center p-4 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:border-purple-300 hover:bg-purple-50">
            <input
              type="checkbox"
              checked={engagementData.reminder1Day}
              onChange={(e) => handleTimingChange('1Day', e.target.checked)}
              className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
            />
            <div className="ml-4 flex items-center flex-1">
              <Calendar className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <span className="font-medium text-gray-900">1 day before election</span>
                <p className="text-sm text-gray-500">Final preparation reminder</p>
              </div>
            </div>
            {engagementData.reminder1Day && (
              <CheckCircle className="w-5 h-5 text-purple-600" />
            )}
          </label>

          <label className="group relative flex items-center p-4 border-2 border-gray-200 rounded-xl cursor-pointer transition-all hover:border-purple-300 hover:bg-purple-50">
            <input
              type="checkbox"
              checked={engagementData.reminderElectionDay}
              onChange={(e) => handleTimingChange('ElectionDay', e.target.checked)}
              className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 focus:ring-2"
            />
            <div className="ml-4 flex items-center flex-1">
              <Calendar className="w-5 h-5 text-gray-400 mr-3" />
              <div>
                <span className="font-medium text-gray-900">Election Day morning</span>
                <p className="text-sm text-gray-500">Same day reminder</p>
              </div>
            </div>
            {engagementData.reminderElectionDay && (
              <CheckCircle className="w-5 h-5 text-purple-600" />
            )}
          </label>
        </div>
      </div>

      {/* Consent Copy */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-xl p-4 mb-8">
        <label className="flex items-start cursor-pointer">
          <input
            type="checkbox"
            checked={isFormValid}
            onChange={() => {}}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 mt-1 flex-shrink-0"
            required
          />
          <p className="ml-3 text-sm text-gray-700 leading-relaxed">
            By selecting reminders, I agree to receive election reminders from 123Vote. 
            Message frequency varies. Opt out anytime.
          </p>
        </label>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() =>  setShowReturnFlow(true)}
          className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleSavePreferences}
          disabled={!isFormValid || electionReminderMutation.isPending}
          className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-medium rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
        >
          {electionReminderMutation.isPending ? 'Saving...' : 'Save My Preferences'}
        </button>
      </div>
    </div>
  );
}
